import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MessageSquare, AlertCircle, CheckCircle } from 'lucide-react'
import { adminApi } from '../../services/api'

export default function AdminFeedbackPage() {
  const [statusFilter, setStatusFilter] = useState<string | undefined>()

  const { data, isLoading } = useQuery({
    queryKey: ['feedback', { status: statusFilter }],
    queryFn: () => adminApi.getFeedback({ status: statusFilter }).then(r => r.data),
  })

  const { data: stats } = useQuery({
    queryKey: ['feedback-stats'],
    queryFn: () => adminApi.getFeedbackStats().then(r => r.data),
  })

  const statuses = [
    { value: undefined, label: 'Wszystkie' },
    { value: 'New', label: 'Nowe' },
    { value: 'InReview', label: 'W analizie' },
    { value: 'Resolved', label: 'Rozwiązane' },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Zgłoszenia użytkowników</h1>
        <p className="text-gray-500">Zarządzaj feedbackiem i zgłoszeniami błędów</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="card p-4">
            <p className="text-sm text-gray-500">Wszystkie</p>
            <p className="text-2xl font-semibold">{stats.total}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">Nowe</p>
            <p className="text-2xl font-semibold text-yellow-600">{stats.new}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">W analizie</p>
            <p className="text-2xl font-semibold text-blue-600">{stats.inReview}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">Rozwiązane</p>
            <p className="text-2xl font-semibold text-green-600">{stats.resolved}</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2">
        {statuses.map((s) => (
          <button
            key={s.value ?? 'all'}
            onClick={() => setStatusFilter(s.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === s.value
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 border'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          </div>
        ) : data?.items?.length ? (
          <div className="divide-y">
            {data.items.map((feedback) => (
              <div key={feedback.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {feedback.status === 'New' ? (
                      <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                    ) : feedback.status === 'Resolved' ? (
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    ) : (
                      <MessageSquare className="w-5 h-5 text-blue-500 mt-0.5" />
                    )}
                    <div>
                      <p className="font-medium">{feedback.type}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {feedback.description}
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        {feedback.userEmail} • {new Date(feedback.createdAt).toLocaleDateString('pl-PL')}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    feedback.status === 'New' ? 'bg-yellow-100 text-yellow-700' :
                    feedback.status === 'InReview' ? 'bg-blue-100 text-blue-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {feedback.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p>Brak zgłoszeń</p>
          </div>
        )}
      </div>
    </div>
  )
}
